import { useEffect, useState, useMemo, useCallback } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Box,
  IconButton,
  Snackbar,
  Grid,
  Button,
  ThemeProvider,
  CssBaseline,
  Tooltip,
  Select,
  Option,
  Chip,
} from '@mui/joy';
import {
  Delete as DeleteIcon,
  Edit,
  KeyboardArrowRight,
  Add,
  Visibility,
} from '@mui/icons-material';
import Breadcrumbs from '@mui/joy/Breadcrumbs';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate, useLocation } from 'react-router-dom';
import { listSettlementGroups, deleteSettlementGroup } from '../../main/API/settlementGroup';
import CustomTextField from '../../main/widgets/CustomTextField';
import DeleteAlert from '../../services/services_manager/views/widgets/DeleteAlert';
import { theme } from '../../main/theme/constants';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { masterApiErrorMessage } from '../../main/utils/masterApiError';
import { isDefaultSettlementGroup } from './widgets/settlementGroupFormPayload';

/** v6: `valueGetter(params)` only. v7+: `(value, row, column, apiRef)`. */
function valueGetterRow(arg1, arg2) {
  if (arg2 !== undefined) return arg2;
  return arg1?.row;
}

export default function SettlementGroupsManagerScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState(null);
  const [results, setResults] = useState([]);
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [successDelete, setSuccessDelete] = useState(false);
  const [failedDelete, setFailedDelete] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState('');
  const [showCreatedSnack, setShowCreatedSnack] = useState(false);
  const [showUpdatedSnack, setShowUpdatedSnack] = useState(false);
  const [searchField, setSearchField] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [separateFilter, setSeparateFilter] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  const fetchData = useCallback(
    async (isSearch = false) => {
      if (!hasPermission(PERMISSIONS.SETTLEMENT_GROUPS_LIST)) {
        setResults([]);
        setData(null);
        return;
      }
      if (isSearch) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
      setIsLoading(true);
      try {
        const page = isSearch ? 1 : paginationModel.page + 1;
        const params = {
          page,
          page_size: paginationModel.pageSize,
          ordering: 'sort_order',
        };
        if (searchField.trim()) params.search = searchField.trim();
        if (activeFilter === 'true' || activeFilter === 'false') {
          params.active = activeFilter === 'true';
        }
        if (separateFilter === 'true' || separateFilter === 'false') {
          params.separate_checkout = separateFilter === 'true';
        }
        const response = await listSettlementGroups(params);
        const actualData = response.data;
        setData(actualData);
        const list = Array.isArray(actualData)
          ? actualData
          : Array.isArray(actualData?.results)
            ? actualData.results
            : [];
        setResults(list);
      } catch (err) {
        console.error(err.message);
        setResults([]);
        setData(null);
      } finally {
        setIsLoading(false);
      }
    },
    [
      hasPermission,
      searchField,
      activeFilter,
      separateFilter,
      paginationModel.page,
      paginationModel.pageSize,
    ]
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleSubmitFilter = async (event) => {
    event.preventDefault();
    await fetchData(true);
  };

  const deleteRow = async (row) => {
    if (!hasPermission(PERMISSIONS.SETTLEMENT_GROUPS_DELETE)) return;
    if (isDefaultSettlementGroup(row)) {
      setFailedDelete(true);
      setDeleteErrorMsg(t('settlementGroups.cannotDeleteDefault'));
      setDeleteAlert(false);
      return;
    }
    setDeleteLoading(true);
    try {
      const response = await deleteSettlementGroup(row.id);
      if (response.status === 200 || response.status === 204) {
        setResults((prev) => prev.filter((item) => item.id !== row.id));
        setSuccessDelete(true);
      } else {
        setFailedDelete(true);
        setDeleteErrorMsg(
          t('settlementGroups.deleteFailedDetail', {
            detail: response.data?.detail ? ` ${response.data.detail}` : '',
          })
        );
      }
    } catch (e) {
      setFailedDelete(true);
      setDeleteErrorMsg(masterApiErrorMessage(e, t) || e.message);
    }
    setDeleteLoading(false);
    setDeleteAlert(false);
  };

  const justCreated = Boolean(location.state && location.state.settlementGroupJustCreated);
  const justUpdated = Boolean(location.state && location.state.settlementGroupJustUpdated);

  useEffect(() => {
    if (!justCreated && !justUpdated) return;
    if (justCreated) setShowCreatedSnack(true);
    if (justUpdated) setShowUpdatedSnack(true);
    fetchData(true);
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: {} }
    );
  }, [
    justCreated,
    justUpdated,
    fetchData,
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ]);

  const columns = useMemo(
    () => [
      { field: 'id', headerName: t('trackingEvent.colId'), width: 70 },
      { field: 'code', headerName: t('settlementGroups.colCode'), flex: 1.4, minWidth: 120 },
      { field: 'name', headerName: t('settlementGroups.colName'), flex: 1.8, minWidth: 150 },
      {
        field: 'name_ar',
        headerName: t('settlementGroups.colNameAr'),
        flex: 1.4,
        minWidth: 120,
      },
      {
        field: 'separate_checkout',
        headerName: t('settlementGroups.colSeparateCheckout'),
        width: 150,
        renderCell: (params) =>
          params.value ? (
            <Chip size="sm" variant="soft" color="warning">
              {t('actions.yes')}
            </Chip>
          ) : (
            <Chip size="sm" variant="soft" color="neutral">
              {t('actions.no')}
            </Chip>
          ),
      },
      {
        field: 'sort_order',
        headerName: t('settlementGroups.colSortOrder'),
        width: 100,
      },
      {
        field: 'active',
        headerName: t('settlementGroups.colActive'),
        width: 90,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.active ? t('serviceManager.priceYes') : t('serviceManager.priceNo');
        },
      },
      {
        field: 'actions',
        headerName: t('trackingEvent.colActions'),
        flex: 1.6,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => {
          const isDefault = isDefaultSettlementGroup(params.row);
          return (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <PermissionGate
                permissions={[
                  PERMISSIONS.SETTLEMENT_GROUPS_VIEW,
                  PERMISSIONS.SETTLEMENT_GROUPS_UPDATE,
                ]}
              >
                <Tooltip title={t('settlementGroups.tooltipOpen')}>
                  <IconButton
                    size="sm"
                    variant="soft"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/settings/settlementgroups/${params.row.id}`);
                    }}
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.SETTLEMENT_GROUPS_UPDATE}>
                <Tooltip title={t('settlementGroups.tooltipEdit')}>
                  <IconButton
                    size="sm"
                    variant="soft"
                    color="neutral"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/settings/settlementgroups/${params.row.id}`);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.SETTLEMENT_GROUPS_DELETE}>
                <Tooltip
                  title={
                    isDefault
                      ? t('settlementGroups.cannotDeleteDefault')
                      : t('settlementGroups.tooltipDelete')
                  }
                >
                  <span>
                    <IconButton
                      size="sm"
                      variant="soft"
                      color="danger"
                      disabled={isDefault}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isDefault) return;
                        setSelectedRow(params.row);
                        setDeleteAlert(true);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </PermissionGate>
            </Box>
          );
        },
      },
    ],
    [t, navigate]
  );

  const rowCount = data?.count != null ? data.count : results.length;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <form onSubmit={handleSubmitFilter}>
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width="100%">
            <Box>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                <Link color="neutral" href="/settings/settlementgroups">
                  {t('nav.settlementGroups')}
                </Link>
                <Link color="neutral" href="/settings/settlementgroups">
                  {t('settlementGroups.breadcrumbManager')}
                </Link>
              </Breadcrumbs>
              <Typography textAlign="start" level="h1">
                {t('settlementGroups.pageTitle')}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'neutral.600', mt: 0.5 }}>
                {t('settlementGroups.pageHint')}
              </Typography>
            </Box>

            <PermissionGate permissions={[PERMISSIONS.SETTLEMENT_GROUPS_LIST]}>
              <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1, mt: 2 }}>
                <Grid xs={12}>
                  <Typography level="h3" textAlign="start">
                    {t('settlementGroups.filterHeading')}
                  </Typography>
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <CustomTextField
                    label={t('settlementGroups.searchLabel')}
                    type="text"
                    value={searchField}
                    onChange={(e) => setSearchField(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Typography level="body-xs" sx={{ mb: 0.5 }}>
                    {t('settlementGroups.activeFilter')}
                  </Typography>
                  <Select
                    value={activeFilter}
                    onChange={(_, v) => setActiveFilter(v || '')}
                    sx={{ width: '100%' }}
                  >
                    <Option value="">{t('settlementGroups.activeAny')}</Option>
                    <Option value="true">{t('serviceManager.priceYes')}</Option>
                    <Option value="false">{t('serviceManager.priceNo')}</Option>
                  </Select>
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Typography level="body-xs" sx={{ mb: 0.5 }}>
                    {t('settlementGroups.separateFilter')}
                  </Typography>
                  <Select
                    value={separateFilter}
                    onChange={(_, v) => setSeparateFilter(v || '')}
                    sx={{ width: '100%' }}
                  >
                    <Option value="">{t('settlementGroups.separateAny')}</Option>
                    <Option value="true">{t('serviceManager.priceYes')}</Option>
                    <Option value="false">{t('serviceManager.priceNo')}</Option>
                  </Select>
                </Grid>
              </Grid>
              <Button type="submit" loading={isLoading}>
                {t('actions.search')}
              </Button>
            </PermissionGate>

            <Grid mt={2} container spacing={2} justifyContent="flex-end">
              <PermissionGate permission={PERMISSIONS.SETTLEMENT_GROUPS_CREATE}>
                <Grid>
                  <Tooltip title={t('settlementGroups.tooltipAdd')}>
                    <IconButton
                      onClick={() => navigate('/settings/settlementgroups/Add')}
                      color="primary"
                      size="large"
                    >
                      <Add sx={{ fontSize: 25 }} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </PermissionGate>
            </Grid>

            <PermissionGate permissions={[PERMISSIONS.SETTLEMENT_GROUPS_LIST]}>
              <Box sx={{ height: '70vh', width: '100%', mt: 2 }}>
                <DataGrid
                  rows={results}
                  columns={columns}
                  pagination
                  paginationMode="server"
                  rowCount={rowCount}
                  paginationModel={paginationModel}
                  pageSizeOptions={[25, 50, 100]}
                  onPaginationModelChange={setPaginationModel}
                  loading={isLoading}
                  onRowDoubleClick={(params) =>
                    navigate(`/settings/settlementgroups/${params.row.id}`)
                  }
                />
              </Box>
            </PermissionGate>

            <DeleteAlert
              open={deleteAlert}
              onClose={() => setDeleteAlert(false)}
              onDelete={() => selectedRow && deleteRow(selectedRow)}
              loading={deleteLoading}
              title={t('settlementGroups.deleteConfirmTitle')}
              body={t('settlementGroups.deleteConfirmBody')}
              confirmLabel={t('actions.delete')}
            />

            <Snackbar
              open={successDelete}
              autoHideDuration={3000}
              onClose={() => setSuccessDelete(false)}
              color="success"
              variant="soft"
            >
              {t('settlementGroups.deletedSuccess')}
            </Snackbar>
            <Snackbar
              open={showCreatedSnack}
              autoHideDuration={3500}
              onClose={() => setShowCreatedSnack(false)}
              color="success"
              variant="soft"
            >
              {t('settlementGroups.snackbarCreated')}
            </Snackbar>
            <Snackbar
              open={showUpdatedSnack}
              autoHideDuration={3500}
              onClose={() => setShowUpdatedSnack(false)}
              color="success"
              variant="soft"
            >
              {t('settlementGroups.snackbarUpdated')}
            </Snackbar>
            <Snackbar
              open={failedDelete}
              autoHideDuration={6000}
              onClose={() => setFailedDelete(false)}
              color="danger"
              variant="soft"
            >
              {deleteErrorMsg || t('settlementGroups.deleteError')}
            </Snackbar>
          </Box>
        </Box>
      </form>
    </ThemeProvider>
  );
}
